import React, { useState, useEffect } from 'react';
import SearchBar from '../components/SearchBar';
import ImageGrid from '../components/ImageGrid';
import ImageModal from '../components/ImageModal';
import { searchImages } from '../config/unsplash';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import useCache from '../hooks/useCache';
import { Search as SearchIcon } from 'lucide-react';

const Search = ({ addToast }) => {
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const cache = useCache(10 * 60 * 1000); // 10 min cache

  useEffect(() => {
    if (searchQuery.trim()) {
      setImages([]);
      setPage(1);
      performSearch(searchQuery, 1);
    } else {
      setImages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const performSearch = async (query, pageNum) => {
    try {
      setIsLoading(true);
      const cacheKey = `search_${query}_${pageNum}`;
      
      // Check cache first
      const cachedData = cache.get(cacheKey);
      if (cachedData) {
        const newImages = cachedData;
        if (pageNum === 1) {
          setImages(newImages);
        } else {
          setImages((prev) => [...prev, ...newImages]);
        }
        setHasMore(newImages.length === 20);
        setPage(pageNum + 1);
        setIsLoading(false);
        return;
      }

      const response = await searchImages(query, pageNum, 20);
      const newImages = response.data.results || [];

      // Store in cache
      cache.set(cacheKey, newImages);

      if (pageNum === 1) {
        setImages(newImages);
      } else {
        setImages((prev) => [...prev, ...newImages]);
      }

      setHasMore(newImages.length === 20);
      setPage(pageNum + 1);
    } catch (error) {
      console.error('Error searching images:', error);
      addToast('Failed to search images', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadMore = () => {
    if (searchQuery.trim() && hasMore && !isLoading) {
      performSearch(searchQuery, page);
    }
  };

  const handleDownload = async (image) => {
    if (!user) {
      addToast('Please login to download images', 'info');
      navigate('/login');
      return;
    }

    try {
      addToast('Downloading...', 'info');
      
      const downloadUrl = `${image.urls.full}?w=1920&q=80&force=true`;
      
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pixelbazar-${image.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      addToast('Image downloaded successfully!', 'success');
    } catch (error) {
      console.error('Download error:', error);
      addToast('Failed to download image', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-black">
      {/* Search Hero Section */}
      <div className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-black border-b border-gray-200/50 dark:border-gray-800/50">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 mb-4">
              <SearchIcon size={24} className="text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-3 tracking-tight">
              Find Your Perfect Image
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-base sm:text-lg font-light">
              Search from millions of high-quality photos instantly
            </p>
          </div>

          {/* Search Bar */}
          <SearchBar onSearch={setSearchQuery} placeholder="Search for images..." />

          {/* Search Tips */}
          {!searchQuery && (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 hover:shadow-md transition-shadow">
                <p className="font-semibold text-gray-900 dark:text-white mb-1 text-sm">Popular</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">nature, sunset, mountain</p>
              </div>
              <div className="p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 hover:shadow-md transition-shadow">
                <p className="font-semibold text-gray-900 dark:text-white mb-1 text-sm">Trending</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">abstract, minimal, urban</p>
              </div>
              <div className="p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 hover:shadow-md transition-shadow">
                <p className="font-semibold text-gray-900 dark:text-white mb-1 text-sm">Categories</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">travel, food, technology</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        {searchQuery && (
          <div className="mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2 tracking-tight">
              Results for <span className="text-blue-600 dark:text-blue-400">"{searchQuery}"</span>
            </h2>
            <p className="text-gray-600 dark:text-gray-400 font-light">
              {images.length > 0 ? `Found ${images.length}+ images` : 'Searching...'}
            </p>
          </div>
        )}

        <ImageGrid
          images={images}
          onImageClick={(image) => {
            setSelectedImage(image);
            setIsModalOpen(true);
          }}
          onDownload={handleDownload}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
          isLoading={isLoading}
        />
      </div>

      {/* Image Modal */}
      <ImageModal
        image={selectedImage}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onDownload={handleDownload}
        addToast={addToast}
      />
    </div>
  );
};

export default Search;
